/**
 * PHARMAKON — pricing engine · the CHIFA split of a sale line (part CNAS / part patient) and the
 * CNAS totals of a facture CHIFA.
 *
 * The CNAS formula, as CHIFA Officine computes it (refonte reference/chifa/FORMATS.md §7, verified on
 * 54 887 real lines; WP-42, CHIFA-AUD-05):
 *   τ     = taux de prise en charge (80 / 100 %)
 *   base  = min(PPA, TR) × quantité   when the TR applies (APPLIC_TARIF = O)
 *         = PPA × quantité            when it does not (APPLIC_TARIF = N)
 *   PS    = τ × base                  part CNAS   — `montant_officine`, reimbursed to the pharmacy
 *   AS    = PPA × quantité − PS       part patient — `montant_assure`, collected at the counter
 * Invariant: AS + PS = PPA × quantité, always (the patient covers what the CNAS does not). The TR is
 * the raw CNAS tarif — never uplifted (REVENUE_MARGIN OP-1) — and is capped at the PPA: the CNAS
 * never reimburses on a base above the public price.
 *
 * Integer centimes, one rounding per line (half away from zero, 03_DATABASE P-7) on the CNAS share;
 * the patient share is the exact remainder. Cross-module X XMD-02-07: the float versions
 * (`toFixed` on IEEE doubles) rounded half-cents the wrong way and could break the invariant by one
 * centime; every caller (Vente CHIFA, instance à terme, « Différer la partie CHIFA », settle, the
 * SPA previews) reads this one function.
 *
 * Per facture (FORMATS.md §7): Total à payer = Σ PS + majoration + FAE. The rates and the FAE are
 * INPUTS (server CONFIG `config/chifa-bordereau.config.js`), never literals of the engine.
 */
import { appliquerPct, diviserArrondi, multiplier, somme, versCentiemesPct, versCentimes, versDinars } from "./money.js";

/** τ in hundredths of a percent × 100 % in hundredths: the divisor of base × τ. */
const BASE_TAUX = 100 * 100;

/**
 * The CHIFA split of one line, in centimes.
 * @param {object} l
 * @param {*} l.ppa        PPA of the unit (DA)
 * @param {*} l.tr         tarif de référence of the unit (DA); 0 / empty = no TR
 * @param {*} l.taux_pec   taux de prise en charge in % (80, 100)
 * @param {number} l.quantite
 * @param {boolean} [l.applique_tarif=true]  APPLIC_TARIF: true (O) = the TR applies (base min(PPA, TR)),
 *        false (N) = the CNAS base is the PPA. Without a TR and with the TR applying, nothing goes to
 *        the CNAS.
 * @returns {{ total: number, officine: number, assure: number }}  centimes: PPA × q, PS, AS
 */
export function partsChifaCentimes({ ppa, tr, taux_pec, quantite, applique_tarif = true }) {
  if (!Number.isSafeInteger(quantite) || quantite < 0) throw new RangeError(`Quantité invalide : ${String(quantite)}`);
  const ppaCentimes = versCentimes(ppa);
  const total = multiplier(ppaCentimes, quantite);
  const baseUnitaire = applique_tarif === false ? ppaCentimes : Math.max(0, Math.min(versCentimes(tr), ppaCentimes));
  const officine = diviserArrondi(BigInt(baseUnitaire) * BigInt(versCentiemesPct(taux_pec)) * BigInt(quantite), BASE_TAUX);
  return { total, officine, assure: total - officine };
}

/**
 * The same split in DA (2 decimals, JS numbers — display / JSON; persist through the centimes).
 * Field names of the server's historical `calcChifa`: montantAS = part patient, montantPS = part CNAS.
 * @param {{ ppa: *, tr: *, taux_pec: *, quantite: number, applique_tarif?: boolean }} l
 * @returns {{ montantAS: number, montantPS: number, total: number }}
 */
export function partsChifa(l) {
  const c = partsChifaCentimes(l);
  return { montantAS: versDinars(c.assure), montantPS: versDinars(c.officine), total: versDinars(c.total) };
}

/**
 * The CNAS totals of one facture CHIFA, estimated the way CHIFA Officine computes them
 * (FORMATS.md §7): the majoration is a share of the part officine of the LOCAL lines, counted only
 * on the facture types that carry it; the FAE is due once per facture.
 *
 * @param {object} f
 * @param {Array<{ officine_centimes: number, local: boolean }>} f.lignes  PS of each line, local flag
 * @param {number|null} [f.type_maj]                 TYPE_MAJ of the facture (null = unknown)
 * @param {number[]} f.types_maj_locale               TYPE_MAJ values on which the local majoration applies
 * @param {*} f.majoration_locale_pct                 the local majoration, % of the part officine
 * @param {*} f.fae                                   FAE of one facture (DA)
 * @returns {{ officine: number, majoration: number, fae: number, total_ps: number }}  centimes —
 *          `total_ps` = Σ PS + majoration + FAE, the amount due by the CNAS (VenteChifa.montant_total_ps)
 */
export function totauxFactureChifaCentimes({ lignes, type_maj = null, types_maj_locale, majoration_locale_pct, fae }) {
  const ls = lignes ?? [];
  const officine = somme(ls.map((l) => l.officine_centimes ?? 0));
  const avecMajoration = type_maj === null || type_maj === undefined || (types_maj_locale ?? []).includes(Number(type_maj));
  const taux = versCentiemesPct(majoration_locale_pct);
  const majoration = avecMajoration
    ? somme(ls.filter((l) => l.local).map((l) => appliquerPct(l.officine_centimes ?? 0, taux)))
    : 0;
  const faeCentimes = ls.length > 0 ? versCentimes(fae) : 0;
  return { officine, majoration, fae: faeCentimes, total_ps: officine + majoration + faeCentimes };
}
